import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, CalendarDays, Pencil, Trash2 } from "lucide-react";
import DeleteDialog from "../components/DeleteDialog";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { WEEKDAYS, fmtDate } from "../lib/format";
import { PageHeader, Loading } from "../components/kit";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function StoreForm({ store, onClose, onDone }) {
  const [f, setF] = useState(store?.id ? store : { name: "", city: "", region: "", code: "", active: true });
  const save = async () => {
    try {
      const body = { name: f.name, city: f.city, region: f.region, code: f.code, active: f.active };
      if (store.id) await api.put(`/stores/${store.id}`, body);
      else await api.post("/stores", body);
      toast.success("Store saved");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const inp = (k, l) => <div className="space-y-1.5"><Label>{l}</Label><Input className="h-11" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`store-${k}-input`} /></div>;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#011E38]">{store.id ? "Edit store" : "New BlueStone store"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">{inp("name", "Store name")}<div className="grid grid-cols-2 gap-3">{inp("city", "City")}{inp("region", "Region / Cluster")}</div>{inp("code", "Store code")}</div>
        <DialogFooter><Button onClick={save} disabled={!f.name} className="bg-[#011E38]" data-testid="store-save-button">Save store</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CalendarForm({ store, onClose, onDone }) {
  const [offs, setOffs] = useState(store.weekly_offs || []);
  const [hol, setHol] = useState(store.holidays || []);
  const [h, setH] = useState({ date: "", name: "", type: "public" });
  const save = async () => {
    try { await api.put(`/stores/${store.id}/calendar`, { weekly_offs: offs, holidays: hol }); toast.success("Working calendar saved"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#011E38]">Working days · {store.name}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div>
            <div className="bs-eyebrow mb-2">Store weekly off</div>
            <div className="grid grid-cols-4 gap-2">{WEEKDAYS.map((d, i) => (
              <label key={d} className="flex items-center gap-2 text-sm"><Checkbox checked={offs.includes(i)} onCheckedChange={(c) => setOffs(c ? [...offs, i] : offs.filter((x) => x !== i))} data-testid={`weekly-off-check-${i}`} />{d.slice(0, 3)}</label>
            ))}</div>
            <p className="text-xs text-slate-500 mt-2">Leave empty if the store is open all days. Staff weekly offs are set per person.</p>
          </div>
          <div>
            <div className="bs-eyebrow mb-2">Holidays</div>
            <div className="flex gap-2">
              <Input type="date" value={h.date} onChange={(e) => setH({ ...h, date: e.target.value })} className="w-40" data-testid="holiday-date-input" />
              <Input placeholder="Name" value={h.name} onChange={(e) => setH({ ...h, name: e.target.value })} data-testid="holiday-name-input" />
              <Select value={h.type} onValueChange={(v) => setH({ ...h, type: v })}>
                <SelectTrigger className="w-28" data-testid="holiday-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["public", "store", "custom"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" disabled={!h.date || !h.name} onClick={() => { setHol([...hol.filter((x) => x.date !== h.date), h].sort((a, b) => a.date.localeCompare(b.date))); setH({ date: "", name: "", type: "public" }); }} data-testid="holiday-add-button"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="mt-3 space-y-1.5 max-h-48 overflow-y-auto">
              {hol.map((x) => (
                <div key={x.date} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid={`holiday-${x.date}`}>
                  <span><b>{fmtDate(x.date)}</b> · {x.name} <span className="text-xs text-slate-500">({x.type})</span></span>
                  <button onClick={() => setHol(hol.filter((y) => y.date !== x.date))}><Trash2 className="w-4 h-4 text-slate-400" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-[#011E38]" data-testid="calendar-save-button">Save calendar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Stores() {
  const { reloadStores, storeId, setStoreId } = useAuth();
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [cal, setCal] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get("/stores", { params: { include_inactive: true } }).then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const done = () => { setEdit(null); setCal(null); load(); reloadStores(); };
  const toggle = async (s, active) => {
    try { await api.put(`/stores/${s.id}`, { name: s.name, city: s.city, region: s.region, code: s.code, active }); done(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="BlueStone network" title="Stores" testid="stores-title"
        right={<Button onClick={() => setEdit({})} className="h-12 rounded-full px-6 bg-[#011E38]" data-testid="add-store-button"><Plus className="w-4 h-4 mr-2" /> Add store</Button>} />
      {!rows ? <Loading /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="stores-grid">
          {rows.map((s) => (
            <div key={s.id} className={`bs-card p-6 ${s.active ? "" : "opacity-60"}`} data-testid={`store-card-${s.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="bs-eyebrow">{s.region || "—"} · {s.code || "—"}</div>
                  <div className="font-display text-2xl font-bold text-[#011E38] mt-1">{s.name}</div>
                  <div className="text-sm text-slate-500">{s.city}</div>
                </div>
                <Switch checked={s.active} onCheckedChange={(c) => toggle(s, c)} data-testid={`store-active-toggle-${s.id}`} />
              </div>
              <div className="text-xs text-slate-500 mt-4">
                Weekly off: {(s.weekly_offs || []).length ? s.weekly_offs.map((i) => WEEKDAYS[i].slice(0, 3)).join(", ") : "None"} · {(s.holidays || []).length} holidays
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => setEdit(s)} data-testid={`edit-store-${s.id}`}><Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit</Button>
                <Button variant="outline" size="sm" onClick={() => setCal(s)} data-testid={`calendar-store-${s.id}`}><CalendarDays className="w-3.5 h-3.5 mr-1.5" /> Working days</Button>
                <Button variant="ghost" size="sm" onClick={() => setDel(s)} data-testid={`delete-store-${s.id}`}><Trash2 className="w-4 h-4 text-red-700" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {edit && <StoreForm store={edit} onClose={() => setEdit(null)} onDone={done} />}
      {cal && <CalendarForm store={cal} onClose={() => setCal(null)} onDone={done} />}
      {del && <DeleteDialog title={`Delete ${del.name}`} word={del.name} path={`/stores/${del.id}`}
        warning="This permanently removes the store with all its entries, returns, targets and closings. Active staff must be transferred or deactivated first."
        onClose={() => setDel(null)} onDone={() => { if (storeId === del.id) setStoreId("all"); setDel(null); done(); }} />}
    </div>
  );
}
